import React, { useState, useEffect } from "react";
import api from "../../../api";

export default function ReportProblemModal({ open, onClose, port }) {
  const [category, setCategory] = useState("number_wont_activate");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [portNumber, setPortNumber] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const categories = [
    { value: "number_wont_activate", label: "Number Won't Activate" },
    { value: "not_receiving_messages", label: "Not Receiving Messages" },
    { value: "receiving_some_but_not_otp", label: "Receiving Some But Not OTP" },
    { value: "something_else", label: "Something Else" },
  ];

  // Convenience flag for conditional UI
  const isNotReceiving = category === "not_receiving_messages";

  // Pre-fill phone/port when port prop changes
  useEffect(() => {
    if (port) {
      setPhoneNumber(port.assigned_number || "");
      setPortNumber(port.port_number || "");
    } else {
      // Reset when opened without a port (global button)
      setPhoneNumber("");
      setPortNumber("");
    }
  }, [port]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const response = await api.post("/api/reports/problem", {
        category,
        phone_number: phoneNumber || null,
        port_number: portNumber || null,
        port_id: port?.id || null,
        detail: details,
      });

      if (response.data?.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          // Reset form
          setCategory("number_wont_activate");
          setPhoneNumber("");
          setPortNumber("");
          setDetails("");
          setSuccess(false);
        }, 2000);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to send report. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              🚨 Report a Problem
              {port && (
                <small className="text-muted d-block mt-1" style={{ fontSize: "0.85rem" }}>
                  for {port.assigned_number || port.port_number}
                </small>
              )}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={submitting}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {success && (
                <div className="alert alert-success">
                  ✅ Report submitted successfully!
                </div>
              )}

              {error && (
                <div className="alert alert-danger">
                  ❌ {error}
                </div>
              )}

              {!success && (
                <>
                  <div className="mb-3">
                    <label className="form-label">
                      What's the issue? <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      required
                      disabled={submitting}
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>

                    {isNotReceiving && (
                      <div className="alert alert-warning mt-2" role="alert">
                        <strong>Heads up:</strong> Please send a text message to the affected
                        number before reporting a problem.
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Phone Number
                      {port ? (
                        <span className="text-muted"> (auto-filled)</span>
                      ) : (
                        <span className="text-muted"> (optional)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 447911123456"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={submitting || !!port}
                      readOnly={!!port}
                      style={port ? { backgroundColor: "#f8f9fa" } : {}}
                    />
                    {port && (
                      <small className="text-muted">
                        Port: {port.port_number || "—"}
                      </small>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Additional Details <span className="text-muted">(optional)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Tell us more about the problem..."
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      disabled={submitting}
                    ></textarea>
                  </div>
                </>
              )}
            </div>

            {!success && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    "Submit Report"
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
